import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import PasswordResetModal from "../components/PasswordResetModal";
import SSOProvidersCard from "../components/SSOProvidersCard";
import { useBranding } from "../context/BrandingContext";
import { formatDateTime, formatRelative, utcMillis } from "../utils/datetime";
import { Customer, PortalCustomerUser, Role, User } from "../types";
import { useResponsiveColumns } from "../hooks/useResponsiveColumns";
import RowActions from "../components/RowActions";

/**
 * Idle sign-out choices, in minutes as strings so they drop straight into a
 * <select>. "" = inherit the appliance default; "0" = never.
 *
 * A fixed list rather than a free number box: the values people actually want
 * are conventional, and a typed field invites "1" (signs everyone out mid-task)
 * or "99999" (reads as configured, behaves as off).
 */
const IDLE_CHOICES: { value: string; label: string }[] = [
  { value: "0", label: "Never" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2 hours" },
  { value: "240", label: "4 hours" },
  { value: "480", label: "8 hours" },
];

const idleLabel = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return "Default";
  const hit = IDLE_CHOICES.find((c) => c.value === String(minutes));
  return hit ? hit.label : `${minutes} minutes`;
};

type TabKey = "staff" | "customers";

export default function Users() {
  const [tab, setTab] = useState<TabKey>("staff");
  const [showSso, setShowSso] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Users</h1>
        <button
          onClick={() => setShowSso(true)}
          className="inline-flex items-center gap-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
            />
          </svg>
          SSO
        </button>
      </div>

      {showSso && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">Single sign-on (SSO)</h2>
              <button
                onClick={() => setShowSso(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-5">
              <SSOProvidersCard />
            </div>
          </div>
        </div>
      )}

      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
        {[
          { key: "staff" as TabKey, label: "Staff" },
          { key: "customers" as TabKey, label: "Customers" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors " +
              (tab === t.key
                ? "border-blue-600 text-blue-700 dark:text-blue-400 font-semibold"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "staff" ? <StaffUsersPanel /> : <CustomerUsersPanel />}
    </div>
  );
}

// ─── Staff (admin/tech/viewer) ─────────────────────────────────────────────

function StaffUsersPanel() {
  const { display_timezone: tz } = useBranding();
  const [users, setUsers] = useState<User[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    role: "technician" as Role,
    can_view_billing: false,
  });
  const [error, setError] = useState("");
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [skillsTarget, setSkillsTarget] = useState<User | null>(null);

  const { sort, toggle } = useSort("username");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Console users",
    useMemo(
      () =>
        users.map((u: any) => ({
          username: u.username,
          email: u.email,
          role: u.role,
          is_active: u.is_active,
          mfa: u.totp_enabled ?? null,
          last_login: u.last_login ?? null,
          // No password field of any kind, hashed or otherwise.
        })),
      [users],
    ),
    undefined,
  );
  const load = () => api.get("/api/v1/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, []);

  const staffSortVal = (u: User, key: string): string | number => {
    switch (key) {
      case "email":
        return (u.email || "").toLowerCase();
      case "role":
        return u.role || "";
      case "billing":
        return u.can_view_billing ? 1 : 0;
      case "status":
        return u.is_active ? 1 : 0;
      case "last_login":
        return utcMillis(u.last_login);
      default:
        return (u.username || "").toLowerCase();
    }
  };
  // One named source of truth per column: the same class goes on the <th>
  // AND its <td>, so the two cannot drift. See hooks/useResponsiveColumns.ts.
  const cols = useResponsiveColumns({
    username: "base",
    email: "md",
    role: "base",
    skills: "lg",
    billing: "lg",
    status: "sm",
    lastLogin: "lg",
    idle: "xl",
    session: "xl",
    actions: "base",
  });

  const sortedUsers = sortRows(users, sort, staffSortVal);
  const userTip = (u: User) =>
    [
      u.username,
      u.email,
      `Role: ${u.role}`,
      `Status: ${u.is_active ? "Active" : "Disabled"}`,
      `Last login: ${formatDateTime(u.last_login, tz)}`,
      (u.skills || []).length ? `Skills: ${(u.skills || []).join(", ")}` : "",
    ]
      .filter(Boolean)
      .join("\n");

  // ── Idle sign-out ───────────────────────────────────────────────────────
  // Appliance-wide default, plus a per-user override. Kept on this page because
  // it is a property of the people, not of the appliance's plumbing.
  const [idleDefault, setIdleDefault] = useState<string>("0");
  const [idleSaving, setIdleSaving] = useState(false);
  const [idleSaved, setIdleSaved] = useState(false);

  useEffect(() => {
    api
      .get("/api/v1/settings")
      .then((r) => setIdleDefault(String(r.data?.idle_timeout_minutes ?? "0")))
      .catch(() => setIdleDefault("0"));
  }, []);

  const saveIdleDefault = async (value: string) => {
    setIdleSaving(true);
    setError("");
    try {
      await api.put("/api/v1/settings/idle_timeout_minutes", { value });
      setIdleDefault(value);
      setIdleSaved(true);
      setTimeout(() => setIdleSaved(false), 2000);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't save the idle timeout");
    } finally {
      setIdleSaving(false);
    }
  };

  const changeIdleTimeout = async (u: User, raw: string) => {
    setError("");
    try {
      // "" means "inherit the appliance default" and must go over the wire as
      // an explicit null — omitting the key would leave the value unchanged.
      const value = raw === "" ? null : Number(raw);
      await api.put(`/api/v1/users/${u.id}`, { idle_timeout_minutes: value });
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't change the idle timeout");
    }
  };

  const changeRole = async (u: User, role: Role) => {
    if (role === u.role) return;
    // Applied on change, so one stray click — or a scroll wheel over the
    // select — silently demotes a colleague, and the first they know is a
    // screen missing half its menu. Somebody reported exactly that.
    //
    // Losing access is the case worth spelling out: a demotion takes areas
    // away, and which ones depends on the Roles & access matrix, so the
    // confirmation says where to look rather than pretending to list them.
    const losing = u.role === "admin" && role !== "admin";
    const message = losing
      ? `Change ${u.username} from Admin to ${role}?

` +
        "They will immediately lose the admin-only areas — Microsoft 365, " +
        "Users, Settings, the audit log and licensing — plus anything switched " +
        "off for that role under Admin → Roles & access."
      : `Change ${u.username} from ${u.role} to ${role}?`;
    if (!window.confirm(message)) {
      // Put the select back: it is a controlled input bound to u.role, but the
      // list only refreshes on success, so without this the dropdown sits on
      // the value the user just declined.
      load();
      return;
    }
    setError("");
    try {
      await api.put(`/api/v1/users/${u.id}`, { role });
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't change role");
    }
  };

  const deleteUser = async (u: User) => {
    if (
      !window.confirm(
        `Delete staff user "${u.username}"? This permanently removes the account. This cannot be undone.`,
      )
    )
      return;
    setError("");
    try {
      await api.delete(`/api/v1/users/${u.id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't delete user");
    }
  };

  const handleResetSubmit = async (
    mode: "auto" | "custom",
    customPassword?: string,
  ): Promise<string | null> => {
    if (!resetTarget) return null;
    const body = mode === "custom" ? { new_password: customPassword } : {};
    const r = await api.post(`/api/v1/users/${resetTarget.id}/reset-password`, body);
    load();
    return r.data.new_password;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/api/v1/users", form);
      setShowForm(false);
      setForm({
        username: "",
        email: "",
        password: "",
        role: "technician",
        can_view_billing: false,
      });
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed");
    }
  };

  const toggleBilling = async (u: User) => {
    if (u.role === "admin") return;
    await api.put(`/api/v1/users/${u.id}`, { can_view_billing: !u.can_view_billing });
    load();
  };

  const toggleActive = async (u: User) => {
    await api.put(`/api/v1/users/${u.id}`, { is_active: !u.is_active });
    load();
  };

  // Force-end a user's single active session. Useful when the session origin
  // below shows an account signed in from somewhere it shouldn't be — cuts
  // access immediately without disabling the account or resetting a password.
  const signOutUser = async (u: User) => {
    if (
      !window.confirm(
        `Sign out "${u.username}"?\n\nTheir current session ends immediately and they'll have to sign in again.`,
      )
    )
      return;
    setError("");
    try {
      await api.post(`/api/v1/users/${u.id}/sign-out`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't sign the user out");
    }
  };

  return (
    <>
      {/* Idle sign-out policy. Above the table because it is the default every
          row inherits — reading it first makes the per-row "Default" meaningful. */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Idle sign-out</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Signs a user out after this long with no keyboard, mouse or touch activity. Measured
              from real interaction in the browser — background refreshes don't count as being at
              the desk — and enforced on the server, so the session is genuinely ended, not just
              hidden. Individual users can override this in the table below.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={idleDefault}
              onChange={(e) => saveIdleDefault(e.target.value)}
              disabled={idleSaving}
              className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              title="Appliance-wide idle sign-out default"
            >
              {IDLE_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {idleSaved && (
              <span className="text-xs text-green-600 dark:text-green-400">Saved</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end mb-3">
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Add Staff User
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Create Staff User</h2>
            <form onSubmit={submit} className="space-y-3">
              {[
                ["Username", "username", "text"],
                ["Email", "email", "email"],
              ].map(([label, field, type]) => (
                <div key={field as string}>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">{label as string}</label>
                  <input
                    type={type as string}
                    value={(form as any)[field as string]}
                    onChange={(e) => setForm({ ...form, [field as string]: e.target.value })}
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  />
                </div>
              ))}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Password</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, password: generateStrongPassword() })}
                    className="border dark:border-gray-600 dark:text-gray-300 text-xs px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 whitespace-nowrap"
                    title="Generate a 20-character strong password"
                  >
                    Auto-generate
                  </button>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Type your own, or click Auto-generate for a 20-char strong password. The user
                  will be required to change it on first login.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="admin">Admin</option>
                  <option value="technician">Technician</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              {form.role !== "admin" && (
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={form.can_view_billing}
                    onChange={(e) => setForm({ ...form, can_view_billing: e.target.checked })}
                  />
                  Grant billing access (can see Billing tab + invoices)
                </label>
              )}
              {form.role === "admin" && (
                <p className="text-xs text-gray-500 dark:text-gray-400">Admins automatically have billing access.</p>
              )}
              {error && <p className="text-sm text-red-500">{error}</p>}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="border dark:border-gray-600 dark:text-gray-300 text-sm px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {skillsTarget && (
        <SkillsEditorModal
          user={skillsTarget}
          onClose={() => setSkillsTarget(null)}
          onSaved={() => {
            setSkillsTarget(null);
            load();
          }}
        />
      )}

      {resetTarget && (
        <PasswordResetModal
          title="Reset password"
          subjectLabel={resetTarget.username}
          description={`Reset the password for ${resetTarget.username}. The user will be required to change it at their next login either way.`}
          onClose={() => setResetTarget(null)}
          onSubmit={handleResetSubmit}
        />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="Username" sortKey="username" sort={sort} onSort={toggle} />
              <SortableTh label="Email" sortKey="email" sort={sort} onSort={toggle} hide={cols.cls("email")} />
              <SortableTh label="Role" sortKey="role" sort={sort} onSort={toggle} />
              <Th className={cols.cls("skills")}>Skills (AI dispatch)</Th>
              <SortableTh label="Billing" sortKey="billing" sort={sort} onSort={toggle} hide={cols.cls("billing")} />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggle} hide={cols.cls("status")} />
              <SortableTh label="Last Login" sortKey="last_login" sort={sort} onSort={toggle} hide={cols.cls("lastLogin")} />
              <Th className={cols.cls("idle")}>Idle sign-out</Th>
              <Th className={cols.cls("session")}>Current session</Th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedUsers.map((u) => (
              <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-900 dark:text-gray-100" title={userTip(u)}>
                <td className="px-4 py-3 font-medium">
                  {u.username}
                  {u.is_sso && (
                    <span
                      className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 font-semibold align-middle"
                      title="Signs in via single sign-on — no local password"
                    >
                      SSO
                    </span>
                  )}
                </td>
                <td className={`px-4 py-3 text-gray-500 dark:text-gray-400 ${cols.cls("email")}`}>{u.email}</td>
                <td className="px-4 py-3">
                  <select
                    value={u.role}
                    onChange={(e) => changeRole(u, e.target.value as Role)}
                    className={`text-xs px-2 py-1 rounded-lg border capitalize font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${u.role === "admin" ? "bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700" : u.role === "technician" ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700" : "bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600"}`}
                    title="Change this user's role"
                  >
                    <option value="admin">Admin</option>
                    <option value="technician">Technician</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </td>
                <td className={`px-4 py-3 ${cols.cls("skills")}`}>
                  <div className="flex items-center gap-1 flex-wrap max-w-xs">
                    {(u.skills || []).slice(0, 4).map((s) => (
                      <span
                        key={s}
                        className="text-[10px] px-1.5 py-0.5 bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-300 rounded font-mono"
                      >
                        {s}
                      </span>
                    ))}
                    {(u.skills || []).length > 4 && (
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">
                        +{(u.skills || []).length - 4}
                      </span>
                    )}
                    {(!u.skills || u.skills.length === 0) && (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 italic">none</span>
                    )}
                    <button
                      onClick={() => setSkillsTarget(u)}
                      className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline ml-1"
                    >
                      edit
                    </button>
                  </div>
                </td>
                <td className={`px-4 py-3 ${cols.cls("billing")}`}>
                  {u.role === "admin" ? (
                    <span
                      className="text-xs text-purple-600 dark:text-purple-400"
                      title="Admins always have billing access"
                    >
                      Always
                    </span>
                  ) : (
                    <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!u.can_view_billing}
                        onChange={() => toggleBilling(u)}
                      />
                      <span className={u.can_view_billing ? "text-emerald-700 dark:text-emerald-400" : "text-gray-400 dark:text-gray-500"}>
                        {u.can_view_billing ? "Yes" : "No"}
                      </span>
                    </label>
                  )}
                </td>
                <td className={`px-4 py-3 ${cols.cls("status")}`}>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
                  >
                    {u.is_active ? "Active" : "Disabled"}
                  </span>
                  {u.must_change_password && (
                    <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300">
                      Must change PW
                    </span>
                  )}
                </td>
                <td className={`px-4 py-3 text-xs text-gray-400 dark:text-gray-500 ${cols.cls("lastLogin")}`}>
                  {formatDateTime(u.last_login, tz)}
                </td>
                <td className={`px-4 py-3 ${cols.cls("idle")}`}>
                  <select
                    value={u.idle_timeout_minutes === null || u.idle_timeout_minutes === undefined
                      ? ""
                      : String(u.idle_timeout_minutes)}
                    onChange={(e) => changeIdleTimeout(u, e.target.value)}
                    className="text-xs px-2 py-1 rounded-lg border bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="How long this user may sit idle before being signed out"
                  >
                    {/* Blank value = inherit. Showing what it resolves to keeps
                        the row honest without making the admin cross-reference
                        the policy card above. */}
                    <option value="">Default ({idleLabel(Number(idleDefault))})</option>
                    {IDLE_CHOICES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </td>
                {/* Origin of this account's ONE active session. Only one is
                    possible per user (newest login wins), so this doubles as
                    "who is signed in right now, and from where" — the view an
                    admin needs to spot a shared login.
                    
                    Gated on session_active, NOT on session_started_at. The
                    origin columns are kept after a forced sign-out (they record
                    where the ended session came from), so keying off them
                    displayed a location and "since 9 hours ago" for a user whose
                    token had already been revoked — it looked exactly like they
                    were still signed in, and an admin who had just pressed Sign
                    out concluded it hadn't worked. */}
                <td className={`px-4 py-3 text-xs ${cols.cls("session")}`}>
                  {u.session_active && u.session_started_at ? (
                    <div className="space-y-0.5">
                      <div className="text-gray-900 dark:text-gray-100 font-medium">
                        {u.session_location || "Unknown location"}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 font-mono">
                        {u.session_ip || "—"}
                      </div>
                      <div
                        className="text-gray-400 dark:text-gray-500"
                        title={u.session_user_agent || undefined}
                      >
                        since {formatRelative(u.session_started_at)}
                      </div>
                    </div>
                  ) : u.session_started_at ? (
                    <div className="space-y-0.5">
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300">
                        Signed out
                      </span>
                      <div className="text-gray-400 dark:text-gray-500">
                        last seen {u.session_location || "unknown location"},{" "}
                        {formatRelative(u.session_started_at)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 italic">
                      No active session
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {/* Four nowrap text links, which on a phone are wider than
                      the rest of the table put together. Kebab below md,
                      inline links from md up - keep the two lists in step. */}
                  <div className="md:hidden flex justify-end">
                    <RowActions
                      actions={[
                        {
                          label: "Sign out",
                          onClick: () => signOutUser(u),
                          hidden: !u.session_active,
                          title: "End this user's active session now",
                        },
                        {
                          label: u.is_sso ? "Password managed by SSO" : "Reset password",
                          onClick: () => setResetTarget(u),
                          // An SSO account has no local password to reset. The
                          // desktop row says so in place of the button, so the
                          // menu says it on a disabled entry rather than
                          // silently omitting the action.
                          disabled: !!u.is_sso,
                          title: u.is_sso
                            ? "This account signs in via SSO - its password is managed by the identity provider, not AeroGauge."
                            : undefined,
                        },
                        {
                          label: u.is_active ? "Disable" : "Enable",
                          onClick: () => toggleActive(u),
                        },
                        { label: "Delete", onClick: () => deleteUser(u), danger: true },
                      ]}
                    />
                  </div>
                  <div className="hidden md:block space-x-3 whitespace-nowrap">
                  {u.session_active && (
                    <button
                      onClick={() => signOutUser(u)}
                      className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
                      title="End this user's active session now"
                    >
                      Sign out
                    </button>
                  )}
                  {u.is_sso ? (
                    <span
                      className="text-xs text-gray-400 dark:text-gray-500"
                      title="This account signs in via SSO — its password is managed by the identity provider, not AeroGauge."
                    >
                      SSO login
                    </span>
                  ) : (
                    <button
                      onClick={() => setResetTarget(u)}
                      className="text-xs text-blue-500 dark:text-blue-400 hover:underline"
                    >
                      Reset Password
                    </button>
                  )}
                  <button
                    onClick={() => toggleActive(u)}
                    className="text-xs text-blue-500 dark:text-blue-400 hover:underline"
                  >
                    {u.is_active ? "Disable" : "Enable"}
                  </button>
                  <button
                    onClick={() => deleteUser(u)}
                    className="text-xs text-red-500 dark:text-red-400 hover:underline"
                  >
                    Delete
                  </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Customer (portal) users ───────────────────────────────────────────────

function CustomerUsersPanel() {
  const { display_timezone: tz } = useBranding();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<number | "">("");
  const [users, setUsers] = useState<PortalCustomerUser[]>([]);
  const [autoInvite, setAutoInvite] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", full_name: "", role: "user" });
  const [inviteError, setInviteError] = useState("");
  const [inviteResult, setInviteResult] = useState<{
    email: string;
    url: string;
    emailed: boolean;
  } | null>(null);
  const [resetTarget, setResetTarget] = useState<PortalCustomerUser | null>(null);

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data));
    api
      .get("/api/v1/customers/portal-settings/auto-invite")
      .then((r) => setAutoInvite(!!r.data.enabled))
      .catch(() => setAutoInvite(false));
  }, []);

  const toggleAutoInvite = async () => {
    const next = !autoInvite;
    setAutoInvite(next);
    try {
      await api.put("/api/v1/customers/portal-settings/auto-invite", { enabled: next });
    } catch {
      setAutoInvite(!next); // revert on failure
    }
  };

  const loadUsers = (cid: number) =>
    api.get(`/api/v1/customers/${cid}/portal-users`).then((r) => setUsers(r.data));

  useEffect(() => {
    if (customerId === "") {
      setUsers([]);
      return;
    }
    loadUsers(customerId as number);
  }, [customerId]);

  const inviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (customerId === "") return;
    setInviteError("");
    if (!inviteForm.email.trim()) {
      setInviteError("Email required");
      return;
    }
    try {
      const r = await api.post(`/api/v1/customers/${customerId}/portal-users`, {
        email: inviteForm.email.trim(),
        full_name: inviteForm.full_name.trim() || undefined,
        role: inviteForm.role,
      });
      setInviteResult({
        email: r.data.user.email,
        url: r.data.set_password_url,
        emailed: !!r.data.invite_sent,
      });
      setInviteForm({ email: "", full_name: "", role: "user" });
      setShowInvite(false);
      loadUsers(customerId as number);
    } catch (err: any) {
      setInviteError(err.response?.data?.detail || "Failed to invite");
    }
  };

  const changePortalRole = async (u: PortalCustomerUser, role: string) => {
    if (role === (u.role || "user")) return;
    await api.put(`/api/v1/customers/portal-users/${u.id}`, { role });
    if (customerId !== "") loadUsers(customerId as number);
  };

  const handleResetSubmit = async (
    mode: "auto" | "custom",
    customPassword?: string,
  ): Promise<string | null> => {
    if (!resetTarget) return null;
    const body = mode === "custom" ? { new_password: customPassword } : {};
    const r = await api.post(
      `/api/v1/customers/portal-users/${resetTarget.id}/reset-password`,
      body,
    );
    if (customerId !== "") loadUsers(customerId as number);
    return r.data.new_password;
  };

  const toggleActive = async (u: PortalCustomerUser) => {
    await api.put(`/api/v1/customers/portal-users/${u.id}`, { is_active: !u.is_active });
    if (customerId !== "") loadUsers(customerId as number);
  };

  const toggleInvoices = async (u: PortalCustomerUser) => {
    await api.put(`/api/v1/customers/portal-users/${u.id}`, {
      can_view_invoices: !u.can_view_invoices,
    });
    if (customerId !== "") loadUsers(customerId as number);
  };

  const removeUser = async (u: PortalCustomerUser) => {
    if (
      !window.confirm(
        `Remove portal user ${u.email}? They will lose access immediately. This cannot be undone.`,
      )
    )
      return;
    await api.delete(`/api/v1/customers/portal-users/${u.id}`);
    if (customerId !== "") loadUsers(customerId as number);
  };

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const { sort, toggle } = useSort("email");
  const custSortVal = (u: PortalCustomerUser, key: string): string | number => {
    switch (key) {
      case "name":
        return (u.full_name || "").toLowerCase();
      case "role":
        return u.role || "user";
      case "status":
        return u.is_active ? 1 : 0;
      case "last_login":
        return utcMillis(u.last_login);
      default:
        return (u.email || "").toLowerCase();
    }
  };
  const sortedUsers = sortRows(users, sort, custSortVal);
  const custTip = (u: PortalCustomerUser) =>
    [
      u.email,
      u.full_name || "",
      `Role: ${u.role || "user"}`,
      `Status: ${u.is_active ? "Active" : "Disabled"}`,
      `Invoices: ${u.can_view_invoices !== false ? "on" : "off"}`,
      `Last login: ${formatDateTime(u.last_login, tz)}`,
    ]
      .filter(Boolean)
      .join("\n");

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Customer</label>
          <select
            value={customerId === "" ? "" : String(customerId)}
            onChange={(e) =>
              setCustomerId(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="border rounded-lg px-3 py-2 text-sm min-w-[16rem] dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          >
            <option value="">— Select a customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {customerId !== "" && (
          <button
            onClick={() => setShowInvite(true)}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Invite Portal User
          </button>
        )}
        <label
          className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 ml-auto cursor-pointer"
          title="When on, the requester on a new ticket is automatically invited to the portal (if they don't already have a login)."
        >
          <input type="checkbox" checked={autoInvite} onChange={toggleAutoInvite} />
          Auto-invite ticket requesters to the portal
        </label>
      </div>

      {customerId === "" ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 text-center text-gray-400 dark:text-gray-500">
          Pick a customer above to see their portal users.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="Email" sortKey="email" sort={sort} onSort={toggle} />
                <SortableTh label="Name" sortKey="name" sort={sort} onSort={toggle} />
                <SortableTh label="Role" sortKey="role" sort={sort} onSort={toggle} />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggle} />
                <SortableTh label="Last Login" sortKey="last_login" sort={sort} onSort={toggle} />
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                    {selectedCustomer?.name} has no portal users yet.
                  </td>
                </tr>
              )}
              {sortedUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-900 dark:text-gray-100" title={custTip(u)}>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">{u.full_name || "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.role || "user"}
                      onChange={(e) => changePortalRole(u, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-lg border font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${(u.role || "user") === "administrator" ? "bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700" : "bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600"}`}
                      title="Change this portal user's role"
                    >
                      <option value="user">User</option>
                      <option value="administrator">Administrator</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
                      >
                        {u.is_active ? "Active" : "Disabled"}
                      </span>
                      {u.must_change_password && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300">
                          Must change PW
                        </span>
                      )}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${u.can_view_invoices !== false ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"}`}
                        title={
                          u.can_view_invoices !== false
                            ? "Sees the Invoices link in the portal"
                            : "Invoices link hidden in the portal"
                        }
                      >
                        {u.can_view_invoices !== false ? "Invoices: on" : "Invoices: off"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(u.last_login, tz)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setResetTarget(u)}
                      className="text-xs text-blue-500 dark:text-blue-400 hover:underline mr-3"
                    >
                      Reset Password
                    </button>
                    <button
                      onClick={() => toggleActive(u)}
                      className="text-xs text-blue-500 dark:text-blue-400 hover:underline mr-3"
                    >
                      {u.is_active ? "Disable" : "Enable"}
                    </button>
                    <button
                      onClick={() => toggleInvoices(u)}
                      className="text-xs text-blue-500 dark:text-blue-400 hover:underline mr-3"
                    >
                      {u.can_view_invoices !== false ? "Deny Invoices" : "Allow Invoices"}
                    </button>
                    <button
                      onClick={() => removeUser(u)}
                      className="text-xs text-red-500 dark:text-red-400 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
              Invite portal user
              {selectedCustomer ? ` — ${selectedCustomer.name}` : ""}
            </h2>
            <form onSubmit={inviteUser} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Email</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  required
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Full Name <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="user">User — view &amp; manage their own tickets</option>
                  <option value="administrator">
                    Administrator — manage their organization
                  </option>
                </select>
              </div>
              {inviteError && <p className="text-sm text-red-500">{inviteError}</p>}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                We'll email a secure link for them to set their own password (no password sent in
                the clear). The link expires in 72 hours.
              </p>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
                >
                  Invite
                </button>
                <button
                  type="button"
                  onClick={() => setShowInvite(false)}
                  className="border dark:border-gray-600 dark:text-gray-300 text-sm px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetTarget && (
        <PasswordResetModal
          title="Reset portal password"
          subjectLabel={resetTarget.email}
          description={`Reset the portal password for ${resetTarget.email}. The user will be required to change it at their next login either way.`}
          onClose={() => setResetTarget(null)}
          onSubmit={handleResetSubmit}
        />
      )}

      {inviteResult && (
        <InviteResultModal result={inviteResult} onClose={() => setInviteResult(null)} />
      )}
    </>
  );
}

function InviteResultModal({
  result,
  onClose,
}: {
  result: { email: string; url: string; emailed: boolean };
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Portal user invited</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {result.emailed ? (
            <>
              A set-password link was emailed to <strong>{result.email}</strong>. They'll set their
              own password and sign in.
            </>
          ) : (
            <>
              We couldn't send the email automatically. Copy this set-password link and send it to{" "}
              <strong>{result.email}</strong> — it expires in 72 hours:
            </>
          )}
        </p>
        <div className="bg-gray-900 text-green-300 font-mono text-xs rounded-lg px-3 py-3 select-all break-all mb-3">
          {result.url}
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => navigator.clipboard?.writeText(result.url)}
            className="border dark:border-gray-600 dark:text-gray-300 text-sm px-4 py-2 rounded-lg"
          >
            Copy link
          </button>
          <button onClick={onClose} className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared bits ───────────────────────────────────────────────────────────

function generateStrongPassword(length = 20): string {
  // Mirrors services.ad_service.generate_password on the server: 20 chars,
  // mixed case + digits + symbols, drawn from a CSPRNG (crypto.getRandomValues).
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const symbols = "!@#$%^&*_-+=";
  const all = lower + upper + digits + symbols;
  const rand = (set: string) => set[Math.floor((window.crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32) * set.length)];
  // Guarantee at least one from each pool, then fill the rest.
  const chars = [rand(lower), rand(upper), rand(digits), rand(symbols)];
  while (chars.length < length) chars.push(rand(all));
  // Fisher-Yates shuffle so the guaranteed chars aren't always at the front.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor((window.crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32) * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

function SkillsEditorModal({
  user,
  onClose,
  onSaved,
}: {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(user.skills || []);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get("/api/v1/users/meta/skill-tags")
      .then((r) => setSuggestions(r.data))
      .catch(() => setSuggestions([]));
  }, []);

  const toggle = (tag: string) => {
    setSelected((s) => (s.includes(tag) ? s.filter((x) => x !== tag) : [...s, tag]));
  };
  const addCustom = () => {
    const v = customInput.trim().toLowerCase();
    if (!v) return;
    if (!selected.includes(v)) setSelected([...selected, v]);
    setCustomInput("");
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/v1/users/${user.id}`, { skills: selected });
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const unusedSuggestions = suggestions.filter((s) => !selected.includes(s));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
          Skills — {user.username}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          The AI dispatcher uses these tags to route tickets. Pick the areas this tech can
          actually handle. Free-text below if you need a tag not in the suggested list.
        </p>

        <div className="mb-3">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Selected ({selected.length}/16)</div>
          <div className="flex flex-wrap gap-1.5 min-h-[28px] p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900">
            {selected.length === 0 && (
              <span className="text-xs text-gray-400 dark:text-gray-500 italic">No skills set yet</span>
            )}
            {selected.map((tag) => (
              <button
                key={tag}
                onClick={() => toggle(tag)}
                className="text-xs px-2 py-0.5 bg-violet-600 text-white rounded font-mono hover:bg-violet-700"
                title="Click to remove"
              >
                {tag} ✕
              </button>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Suggested</div>
          <div className="flex flex-wrap gap-1.5">
            {unusedSuggestions.map((tag) => (
              <button
                key={tag}
                onClick={() => toggle(tag)}
                className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 hover:bg-violet-100 dark:hover:bg-violet-900/40 text-gray-700 dark:text-gray-300 rounded font-mono"
              >
                + {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Custom tag</div>
          <div className="flex gap-2">
            <input
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustom();
                }
              }}
              className="flex-1 border rounded px-2 py-1 text-xs font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              placeholder="e.g. cisco-asa"
            />
            <button
              onClick={addCustom}
              className="text-xs px-3 py-1 border dark:border-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Add
            </button>
          </div>
        </div>

        {error && (
          <div className="text-xs bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-300 p-2 rounded mb-3">{error}</div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border dark:border-gray-600 dark:text-gray-300 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  /** Responsive `hidden md:table-cell` etc. Must match the column's `<td>`. */
  className?: string;
}) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${className}`}>
      {children}
    </th>
  );
}

// ─── Sortable-column helpers (backlog #40) ─────────────────────────────────

type SortState = { key: string; dir: "asc" | "desc" };

function useSort(defaultKey: string) {
  const [sort, setSort] = useState<SortState>({ key: defaultKey, dir: "asc" });
  const toggle = (key: string) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );
  return { sort, toggle };
}

function sortRows<T>(
  rows: T[],
  sort: SortState,
  valueOf: (row: T, key: string) => string | number,
): T[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = valueOf(a, sort.key);
    const vb = valueOf(b, sort.key);
    if (va < vb) return -dir;
    if (va > vb) return dir;
    return 0;
  });
}

function SortableTh({
  label,
  sortKey,
  sort,
  onSort,
  hide = "",
}: {
  label: string;
  sortKey: string;
  sort: SortState;
  onSort: (k: string) => void;
  /** Responsive `hidden md:table-cell` etc. MUST match the column's `<td>`. */
  hide?: string;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase ${hide}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="group inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
        title={`Sort by ${label}`}
      >
        {label}
        <span className={active ? "text-gray-700 dark:text-gray-300" : "text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500"}>
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

